import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { discussionService } from '../services/api';
import useAuthStore from '../store/authStore';
import { Button, Badge, Textarea, Input } from './ui';

function timeAgo(dateStr, t) {
  const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
  if (diff < 60) return t('just now');
  if (diff < 3600) return t('{{count}}m ago', { count: Math.floor(diff / 60) });
  if (diff < 86400) return t('{{count}}h ago', { count: Math.floor(diff / 3600) });
  return t('{{count}}d ago', { count: Math.floor(diff / 86400) });
}

function ThreadCard({ thread, isTeacher, onChanged, t }) {
  const { user } = useAuthStore();
  const [expanded, setExpanded] = useState(false);
  const [replies, setReplies] = useState(thread.replies || []);
  const [loadingReplies, setLoadingReplies] = useState(false);
  const [replyBody, setReplyBody] = useState('');
  const [posting, setPosting] = useState(false);

  const canModerate = isTeacher || thread.author.id === user?.id;

  const loadReplies = useCallback(async () => {
    setLoadingReplies(true);
    try {
      const res = await discussionService.getReplies(thread.id);
      setReplies(res.data?.results || res.data || []);
    } catch (err) {
      console.warn('Failed to load replies', err);
    } finally {
      setLoadingReplies(false);
    }
  }, [thread.id]);

  const toggleExpand = () => {
    const next = !expanded;
    setExpanded(next);
    if (next) loadReplies();
  };

  const handleReply = async () => {
    if (!replyBody.trim()) return;
    setPosting(true);
    try {
      await discussionService.createReply({ thread: thread.id, body: replyBody });
      setReplyBody('');
      await loadReplies();
      onChanged();
    } catch (err) {
      console.error('Failed to post reply', err);
    } finally {
      setPosting(false);
    }
  };

  const handleResolve = async () => {
    try {
      await discussionService.resolveThread(thread.id, !thread.is_resolved);
      onChanged();
    } catch (err) {
      console.error('Failed to update thread', err);
    }
  };

  const handleDelete = async () => {
    try {
      await discussionService.deleteThread(thread.id);
      onChanged();
    } catch (err) {
      console.error('Failed to delete thread', err);
    }
  };

  return (
    <div className="border border-gray-200 rounded-lg p-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-medium text-sm text-gray-900">{thread.title}</h3>
            {thread.is_resolved && <Badge tone="green">{t('Resolved')}</Badge>}
          </div>
          {thread.body && <p className="text-xs text-gray-600 mt-1 whitespace-pre-line">{thread.body}</p>}
          <div className="text-xs text-gray-400 mt-1.5">
            {thread.author.first_name} {thread.author.last_name} • {timeAgo(thread.created_at, t)}
          </div>
        </div>
        {canModerate && (
          <div className="flex items-center gap-2 shrink-0">
            <button onClick={handleResolve} className="text-xs text-gray-400 hover:text-emerald-600" title={t('Toggle resolved')}>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </button>
            <button onClick={handleDelete} className="text-xs text-gray-400 hover:text-red-500" title={t('Delete')}>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </div>
        )}
      </div>

      <button onClick={toggleExpand} className="mt-2 text-xs font-medium text-primary-600 hover:underline flex items-center gap-1">
        {t('{{count}} replies', { count: thread.replies_count })}
        <svg className={`w-3 h-3 transition-transform ${expanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {expanded && (
        <div className="mt-3 space-y-2 border-t border-gray-100 pt-3">
          {loadingReplies ? (
            <p className="text-xs text-gray-400">{t('Loading...')}</p>
          ) : (
            replies.map((r) => (
              <div key={r.id} className="bg-gray-50 rounded-lg px-3 py-2">
                <div className="text-xs font-medium text-gray-800">
                  {r.author.first_name} {r.author.last_name}
                  <span className="font-normal text-gray-400 ms-2">{timeAgo(r.created_at, t)}</span>
                </div>
                <p className="text-xs text-gray-600 mt-1 whitespace-pre-line">{r.body}</p>
              </div>
            ))
          )}
          <div className="flex items-center gap-2 pt-1">
            <input
              type="text"
              placeholder={t('Write a reply...')}
              value={replyBody}
              onChange={(e) => setReplyBody(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleReply()}
              className="flex-1 text-xs border border-gray-200 rounded-lg px-3 py-1.5"
            />
            <Button size="sm" onClick={handleReply} loading={posting} disabled={!replyBody.trim()}>
              {t('Reply')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function LessonDiscussions({ lessonId, isTeacher }) {
  const { t } = useTranslation();
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAsk, setShowAsk] = useState(false);
  const [askTitle, setAskTitle] = useState('');
  const [askBody, setAskBody] = useState('');
  const [posting, setPosting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await discussionService.getThreads(lessonId);
      setThreads(res.data?.results || res.data || []);
    } catch (err) {
      console.warn('Failed to load discussion threads', err);
    } finally {
      setLoading(false);
    }
  }, [lessonId]);

  useEffect(() => { load(); }, [load]);

  const handleAsk = async () => {
    if (!askTitle.trim()) return;
    setPosting(true);
    try {
      await discussionService.createThread({ lesson: lessonId, title: askTitle, body: askBody });
      setAskTitle('');
      setAskBody('');
      setShowAsk(false);
      await load();
    } catch (err) {
      console.error('Failed to post question', err);
    } finally {
      setPosting(false);
    }
  };

  if (loading) return null;

  return (
    <div className="bg-white rounded-xl shadow-md p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold text-gray-800 flex items-center">
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
          {t('Discussion')}
        </h2>
        <button onClick={() => setShowAsk((v) => !v)} className="text-xs font-medium text-primary-600 hover:underline">
          + {t('Ask a Question')}
        </button>
      </div>

      {showAsk && (
        <div className="border border-gray-200 rounded-lg p-3 mb-4 space-y-2">
          <Input placeholder={t('Question title')} value={askTitle} onChange={(e) => setAskTitle(e.target.value)} autoFocus />
          <Textarea rows={2} placeholder={t('Add more detail (optional)')} value={askBody} onChange={(e) => setAskBody(e.target.value)} />
          <Button size="sm" onClick={handleAsk} loading={posting} disabled={!askTitle.trim()}>
            {t('Post Question')}
          </Button>
        </div>
      )}

      {threads.length === 0 ? (
        <p className="text-sm text-gray-400">{t('No questions yet. Be the first to ask!')}</p>
      ) : (
        <div className="space-y-3">
          {threads.map((thread) => (
            <ThreadCard key={thread.id} thread={thread} isTeacher={isTeacher} onChanged={load} t={t} />
          ))}
        </div>
      )}
    </div>
  );
}
